import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Stethoscope,
  CalendarRange,
  ClipboardList,
  BarChart3,
  MessageCircle,
  CreditCard,
  Palette,
  Settings as SettingsIcon,
  LogOut,
  Menu,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";

const NAV = [
  { to: "/hospital", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/hospital/doctors", label: "Doctors", icon: Stethoscope, testid: "nav-doctors" },
  { to: "/hospital/schedules", label: "Schedules", icon: CalendarRange, testid: "nav-schedules" },
  { to: "/hospital/appointments", label: "Appointments", icon: ClipboardList, testid: "nav-appointments" },
  { to: "/hospital/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/hospital/integrations", label: "WhatsApp", icon: MessageCircle, testid: "nav-integrations" },
  { to: "/hospital/payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
  { to: "/hospital/branding", label: "Branding", icon: Palette, testid: "nav-branding" },
  { to: "/hospital/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function HospitalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [hospital, setHospital] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/hospital/me").then((r) => setHospital(r.data)).catch(() => {});
  }, []);

  const NavList = ({ onClick }) => (
    <nav className="space-y-1">
      {NAV.map((n) => (
        <NavLink
          key={n.to} to={n.to} end={n.end} onClick={onClick}
          data-testid={n.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive ? "bg-teal-500/10 text-teal-300 border border-teal-500/20"
                       : "text-mb-fg2 hover:text-white hover:bg-mb-surface2 border border-transparent"
            }`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const Sidebar = ({ onClick }) => (
    <div className="h-full flex flex-col">
      <div className="px-5 py-6 border-b border-mb-border">
        <NavLink
  to="/hospital"
  onClick={onClick}
  aria-label="Go to hospital dashboard"
  className="flex items-center gap-3 rounded-lg transition-opacity hover:opacity-90"
>
  {hospital?.logo_url ? (
    <img
      src={hospital.logo_url}
      alt={`${hospital?.name || "Hospital"} logo`}
      data-testid="sidebar-hospital-logo"
      className="h-10 w-10 shrink-0 rounded-xl bg-white/5 object-contain p-1"
    />
  ) : (
    <img
      src="/medibooks-logo.png"
      alt="MediBooks"
      data-testid="sidebar-medibooks-logo"
      className="h-10 w-10 shrink-0 rounded-xl bg-white object-contain p-1"
    />
  )}

  <div className="min-w-0">
    <div className="truncate text-sm font-semibold tracking-wide">
      {hospital?.name || "MediBooks"}
    </div>

    <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
      Hospital Admin
    </div>
  </div>
</NavLink>
        <div className="mt-5 px-3 py-3 rounded-lg border border-mb-border bg-mb-surface" data-testid="hospital-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">Hospital</div>
          <div className="mt-1 text-sm font-medium text-white truncate">{hospital?.name || "—"}</div>
          <div className="mt-0.5 text-xs text-mb-fg2 truncate">{hospital?.slug}</div>
        </div>
      </div>
      <div className="flex-1 px-3 py-5 overflow-y-auto"><NavList onClick={onClick} /></div>
      <div className="px-3 py-4 border-t border-mb-border">
        <div className="px-3 pb-3 text-xs text-mb-fg2 truncate" data-testid="sidebar-user-email">{user?.email}</div>
        <Button onClick={onLogout} data-testid="sidebar-logout"
          variant="outline" className="w-full bg-transparent border-mb-border hover:bg-mb-surface2 text-white">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-mb-bg text-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-72 flex-col border-r border-mb-border bg-mb-bg">
        <Sidebar />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="px-5 lg:px-8 h-16 border-b border-mb-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button data-testid="mobile-menu-button" variant="ghost" className="md:hidden h-9 w-9 p-0 hover:bg-mb-surface2">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-mb-bg border-mb-border text-white p-0 w-72">
                <Sidebar onClick={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="text-sm text-mb-fg2">
              <span className="text-mb-fg2">/</span> hospital admin <span className="text-mb-fg2 mx-1">/</span>{" "}
              <span className="text-white font-medium" data-testid="topbar-hospital-name">{hospital?.name || "…"}</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-mb-fg2" data-testid="topbar-user">
              {user?.full_name}
            </span>
          </div>
        </header>
        <main className="flex-1 p-5 lg:p-8 overflow-x-hidden">
          <Outlet context={{ hospital, refreshHospital: () =>
            api.get("/hospital/me").then((r) => setHospital(r.data)).catch(() => {}) }} />
        </main>
      </div>
    </div>
  );
}
